/* component-predictive-search.js — <predictive-search>
   Debounced Shopify Predictive Search (/search/suggest.json).
   - encodeURIComponent on the query, AbortController to cancel in-flight requests.
   - products + collections + pages.
   - EXCLUSIONS APPLIED BEFORE LIMIT: products tagged with the exclusion tag
     (data-exclude-tag) are filtered out first, then the list is capped to data-limit.
   - Opens via kk:drawer:open {id:'search'} (published by the header search button),
     closes on scrim/close/Escape. Focus is trapped to the input on open.
   The input is always visible; no results markup is injected until the user types. */
(function () {
  'use strict';

  var DEBOUNCE = 250;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-psearch-input]');
      this.resultsEl = this.querySelector('[data-psearch-results]');
      this.loader = this.querySelector('[data-psearch-loader]');
      this.emptyEl = this.querySelector('[data-psearch-empty]');
      this.viewAll = this.querySelector('[data-psearch-viewall]');
      this.form = this.querySelector('[data-psearch-form]');
      this.scrim = this.querySelector('[data-psearch-scrim]');
      this.closeBtn = this.querySelector('[data-psearch-close]');

      this.excludeTag = (this.getAttribute('data-exclude-tag') || '').trim().toLowerCase();
      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 6;
      this.searchRoot = this._root();

      this._onInput = this._debounce(this._search.bind(this), DEBOUNCE);
      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('search', this._onInput); // clear (x) fires 'search'

      if (this.scrim) this.scrim.addEventListener('click', this.close.bind(this));
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.close.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));

      // Open trigger from the header search button.
      if (window.KK && window.KK.pubsub) {
        this._unsub = window.KK.pubsub.subscribe('kk:drawer:open', function (p) {
          if (p && p.id === 'search') this.open();
        }.bind(this));
      }
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
      if (this._controller) this._controller.abort();
    }

    _root() {
      try {
        if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
          return window.Shopify.routes.root.replace(/\/$/, '');
        }
      } catch (e) {}
      return '';
    }

    open() {
      this.hidden = false;
      document.documentElement.style.setProperty('overflow', 'hidden');
      // Focus after paint so the field is ready.
      requestAnimationFrame(function () { this.input.focus(); }.bind(this));
    }

    close() {
      this.hidden = true;
      document.documentElement.style.removeProperty('overflow');
      if (window.KK && window.KK.pubsub) window.KK.pubsub.publish('kk:drawer:close', { id: 'search' });
    }

    _onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
    }

    _debounce(fn, ms) {
      var t;
      return function () { clearTimeout(t); t = setTimeout(fn, ms); };
    }

    _search() {
      var q = this.input.value.trim();
      this.input.setAttribute('aria-expanded', q ? 'true' : 'false');
      if (!q) { this._clear(); return; }

      if (this._controller) this._controller.abort();
      this._controller = new AbortController();

      this._setLoading(true);

      var resources = 'product,collection,page';
      // Ask for extra products so exclusions don't starve the visible list.
      var url = this.searchRoot + '/search/suggest.json'
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=' + resources
        + '&resources[limit]=' + Math.min(this.limit + 4, 10)
        + '&resources[options][unavailable_products]=last';

      fetch(url, { signal: this._controller.signal, headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { this._render(data, q); })
        .catch(function (err) { if (err.name !== 'AbortError') this._clear(); }.bind(this))
        .finally(function () { this._setLoading(false); }.bind(this));
    }

    _setLoading(on) {
      if (this.loader) this.loader.hidden = !on;
    }

    _clear() {
      this._eachGroup(function (g) { g.hidden = true; g.querySelector('[data-psearch-list]').innerHTML = ''; });
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.viewAll) this.viewAll.hidden = true;
    }

    _eachGroup(fn) {
      var groups = this.querySelectorAll('[data-psearch-group]');
      for (var i = 0; i < groups.length; i++) fn(groups[i]);
    }

    _render(data, q) {
      var res = (data && data.resources && data.resources.results) || {};
      var products = res.products || [];
      var collections = res.collections || [];
      var pages = res.pages || [];

      // EXCLUSIONS BEFORE LIMIT.
      if (this.excludeTag) {
        products = products.filter(function (p) {
          var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
          return tags.indexOf(this.excludeTag) === -1;
        }.bind(this));
      }
      products = products.slice(0, this.limit);
      collections = collections.slice(0, 4);
      pages = pages.slice(0, 4);

      var total = products.length + collections.length + pages.length;

      this._fillProducts(products);
      this._fillLinks('collections', collections);
      this._fillLinks('pages', pages);

      if (this.emptyEl) {
        if (total === 0) {
          this.emptyEl.hidden = false;
          this.emptyEl.textContent = this.emptyEl.getAttribute('data-empty-text') || '';
          if (!this.emptyEl.textContent) this.emptyEl.textContent = '—';
        } else {
          this.emptyEl.hidden = true;
        }
      }

      if (this.viewAll) {
        if (total > 0) {
          this.viewAll.hidden = false;
          this.viewAll.href = this.searchRoot + '/search?q=' + encodeURIComponent(q);
        } else {
          this.viewAll.hidden = true;
        }
      }
    }

    _fillProducts(items) {
      var group = this.querySelector('[data-psearch-group="products"]');
      var list = group.querySelector('[data-psearch-list]');
      list.innerHTML = '';
      if (!items.length) { group.hidden = true; return; }
      group.hidden = false;
      items.forEach(function (p) {
        var li = document.createElement('li');
        li.className = 'kk-psearch__item kk-psearch__item--product';
        li.setAttribute('role', 'option');
        var img = (p.featured_image && p.featured_image.url) || (p.image) || '';
        var thumb = img ? this._thumb(img) : '';
        var price = p.price != null ? this._money(p.price) : '';
        li.innerHTML =
          '<a class="kk-psearch__link" href="' + this._safe(p.url) + '">'
          + (thumb ? '<img class="kk-psearch__thumb" src="' + this._safe(thumb) + '" alt="" width="48" height="48" loading="lazy">' : '<span class="kk-psearch__thumb kk-psearch__thumb--empty"></span>')
          + '<span class="kk-psearch__meta">'
          + '<span class="kk-psearch__title">' + this._esc(p.title) + '</span>'
          + (price ? '<span class="kk-psearch__price">' + price + '</span>' : '')
          + '</span></a>';
        list.appendChild(li);
      }.bind(this));
    }

    _fillLinks(kind, items) {
      var group = this.querySelector('[data-psearch-group="' + kind + '"]');
      var list = group.querySelector('[data-psearch-list]');
      list.innerHTML = '';
      if (!items.length) { group.hidden = true; return; }
      group.hidden = false;
      items.forEach(function (it) {
        var li = document.createElement('li');
        li.className = 'kk-psearch__item';
        li.setAttribute('role', 'option');
        li.innerHTML = '<a class="kk-psearch__link kk-psearch__link--text" href="' + this._safe(it.url) + '">' + this._esc(it.title) + '</a>';
        list.appendChild(li);
      }.bind(this));
    }

    _money(price) {
      // suggest.json returns product price as a decimal string (e.g. "39.00").
      // KK.money expects integer cents, so convert only when we got a number.
      if (typeof price === 'string') {
        var n = parseFloat(price);
        if (isNaN(n)) return this._esc(price);
        if (window.KK && window.KK.money) return window.KK.money(Math.round(n * 100));
        return n.toFixed(2);
      }
      if (window.KK && window.KK.money) return window.KK.money(price);
      return ((Number(price) || 0) / 100).toFixed(2);
    }

    _thumb(url) {
      // Request a small Shopify CDN render. Works with both ?v= and bare URLs.
      var s = String(url);
      if (s.indexOf('cdn.shop') === -1 && s.indexOf('/cdn/') === -1) return s;
      return s + (s.indexOf('?') === -1 ? '?' : '&') + 'width=96';
    }

    _esc(s) {
      var d = document.createElement('div');
      d.textContent = s == null ? '' : String(s);
      return d.innerHTML;
    }

    _safe(url) {
      // Allow only same-origin relative or http(s) URLs.
      var s = String(url == null ? '' : url);
      if (/^\s*javascript:/i.test(s)) return '#';
      return this._esc(s);
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
